// frontend/src/components/employee/availability/availabilityFirestore.js

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from '../../../../firebaseConfig';

const BLOCKS = "tutor_availability_blocks";
const PAYROLL = "payroll_records";
const UI_DOC = doc(db, "availabilityUI", "employee");

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj || {}, key);
}

function isScheduleDayOverrideDoc(id, data = {}) {
  if (typeof id === "string" && id.startsWith("day_")) return true;
  if (data?.source === "schedule_day_settings") return true;
  if (hasOwn(data, "unavailable") || hasOwn(data, "hidden")) return true;
  return false;
}

function toDateMaybe(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts?.toDate === "function") return ts.toDate();
  return null;
}

function timestampToMillis(value) {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function dateKeyLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function shouldReplaceAvailabilityRecord(current, candidate) {
  if (!current) return true;

  const currentIsOverride = isScheduleDayOverrideDoc(current?.id, current);
  const candidateIsOverride = isScheduleDayOverrideDoc(candidate?.id, candidate);
  if (candidateIsOverride !== currentIsOverride) {
    return candidateIsOverride;
  }

  const currentUpdated = timestampToMillis(current?.updatedAt);
  const candidateUpdated = timestampToMillis(candidate?.updatedAt);
  if (candidateUpdated !== currentUpdated) {
    return candidateUpdated > currentUpdated;
  }

  const currentStart = toDateMaybe(current?.start)?.getTime() || 0;
  const candidateStart = toDateMaybe(candidate?.start)?.getTime() || 0;
  return candidateStart > currentStart;
}

// ---------- UI Settings (colors + order) ----------
export async function fetchAvailabilityUI() {
  const snap = await getDoc(UI_DOC);
  if (!snap.exists()) return { tutorOrder: [], tutorColors: {} };
  const data = snap.data() || {};
  return {
    tutorOrder: Array.isArray(data.tutorOrder) ? data.tutorOrder : [],
    tutorColors: data.tutorColors && typeof data.tutorColors === "object" ? data.tutorColors : {},
  };
}

export async function saveAvailabilityUI(partial) {
  // merge
  const clean = {};
  if (partial.tutorOrder !== undefined) clean.tutorOrder = partial.tutorOrder;
  if (partial.tutorColors !== undefined) clean.tutorColors = partial.tutorColors;
  await setDoc(UI_DOC, { ...clean, updatedAt: serverTimestamp() }, { merge: true });
}

// ---------- Blocks ----------

// Fetch blocks for a specific tutor in [start, end)
export async function fetchTutorBlocksInRange(uid, rangeStartDate, rangeEndDateExclusive) {
  const startTs = Timestamp.fromDate(rangeStartDate);
  const endTs = Timestamp.fromDate(rangeEndDateExclusive);

  const q = query(
    collection(db, BLOCKS),
    where("uid", "==", uid),
    where("start", ">=", startTs),
    where("start", "<", endTs),
    orderBy("start", "asc")
  );

  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const byTutorDay = new Map();
  rows.forEach((row) => {
    const start = toDateMaybe(row?.start);
    if (!start || !row?.uid) return;
    const key = `${row.uid}|${dateKeyLocal(start)}`;
    const current = byTutorDay.get(key);
    if (shouldReplaceAvailabilityRecord(current, row)) {
      byTutorDay.set(key, row);
    }
  });

  return [...byTutorDay.values()]
    .filter((row) => !row?.unavailable)
    .sort((a, b) => {
      const aStart = toDateMaybe(a?.start)?.getTime() || 0;
      const bStart = toDateMaybe(b?.start)?.getTime() || 0;
      return aStart - bStart;
    });
}

// Fallback: Fetch ALL blocks (useful if "All" is selected, though UI might be cluttered)
export async function fetchBlocksInRange(rangeStartDate, rangeEndDateExclusive) {
  const startTs = Timestamp.fromDate(rangeStartDate);
  const endTs = Timestamp.fromDate(rangeEndDateExclusive);

  const q = query(
    collection(db, BLOCKS),
    where("start", ">=", startTs),
    where("start", "<", endTs),
    orderBy("start", "asc")
  );

  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const byTutorDay = new Map();
  rows.forEach((row) => {
    const start = toDateMaybe(row?.start);
    if (!start || !row?.uid) return;
    const key = `${row.uid}|${dateKeyLocal(start)}`;
    const current = byTutorDay.get(key);
    if (shouldReplaceAvailabilityRecord(current, row)) {
      byTutorDay.set(key, row);
    }
  });

  return [...byTutorDay.values()]
    .filter((row) => !row?.unavailable)
    .sort((a, b) => {
      const uidCompare = String(a?.uid || "").localeCompare(String(b?.uid || ""));
      if (uidCompare !== 0) return uidCompare;
      const aStart = toDateMaybe(a?.start)?.getTime() || 0;
      const bStart = toDateMaybe(b?.start)?.getTime() || 0;
      return aStart - bStart;
    });
}

export async function fetchTutorPayrollSegmentsInRange(uid, rangeStartDate, rangeEndDateExclusive) {
  const startTs = Timestamp.fromDate(rangeStartDate);
  const endTs = Timestamp.fromDate(rangeEndDateExclusive);

  const q = query(
    collection(db, PAYROLL),
    where("uid", "==", uid),
    where("startTime", ">=", startTs),
    where("startTime", "<", endTs),
    orderBy("startTime", "asc")
  );

  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((row) => row?.startTime && row?.endTime);
}

export async function createAvailabilityBlock({ uid, startDate, endDate }) {
  const dayBase = new Date(startDate);
  dayBase.setHours(0, 0, 0, 0);
  const overrideDocId = `day_${dateKeyLocal(dayBase)}_${uid}`;
  const overrideRef = doc(db, BLOCKS, overrideDocId);
  const startMin = startDate.getHours() * 60 + startDate.getMinutes();
  const endMin = endDate.getHours() * 60 + endDate.getMinutes();
  await setDoc(
    overrideRef,
    {
      uid,
      start: Timestamp.fromDate(startDate),
      end: Timestamp.fromDate(endDate),
      startMin,
      endMin,
      unavailable: false,
      hidden: false,
      source: "schedule_day_settings",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  return overrideDocId;
  /* Legacy shape retained for backwards compatibility:
  const ref = await addDoc(collection(db, BLOCKS), {
    uid,
    start: Timestamp.fromDate(startDate),
    end: Timestamp.fromDate(endDate),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id; */
}

export async function updateAvailabilityBlock(id, { startDate, endDate }) {
  const patch = {
    start: Timestamp.fromDate(startDate),
    end: Timestamp.fromDate(endDate),
    startMin: startDate.getHours() * 60 + startDate.getMinutes(),
    endMin: endDate.getHours() * 60 + endDate.getMinutes(),
    updatedAt: serverTimestamp(),
  };

  if (typeof id === "string" && id.startsWith("day_")) {
    patch.unavailable = false;
    patch.hidden = false;
    patch.source = "schedule_day_settings";
  }

  await updateDoc(doc(db, BLOCKS, id), patch);
}

export async function deleteAvailabilityBlock(id) {
  if (typeof id === "string" && id.startsWith("day_")) {
    await updateDoc(doc(db, BLOCKS, id), {
      unavailable: true,
      hidden: true,
      source: "schedule_day_settings",
      updatedAt: serverTimestamp(),
    });
    return;
  }

  await deleteDoc(doc(db, BLOCKS, id));
}
